import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { ApiClient } from '../../api';
import type { MsrAnlageZeile, MsrAspZeile, MsrDatenpunktZeile, MsrSeite } from './typen';
import { ART, GEWERK, HERKUNFT_FARBE, herkunftMarke, z } from './gemeinsam';
import {
  DATENPUNKTE_FILTER_LEER, Filterleiste, type DatenpunkteFilter,
} from './Filterleiste';
import { DatenpunktlisteVdi } from './DatenpunktlisteVdi';
import { DataTable, EmptyState, StatusBadge, type Spalte } from '../../muster';
import { RANG_SEITE, msrZiele, useMsrExport } from './kopf';

const GROESSE = 200;

/**
 * Reiter „Datenpunktliste" — zwei Ansichten in `?ansicht=` (Standard
 * „flach"): die bisherige Filterleiste mit seitenweiser Tabelle, oder „VDI
 * 3814" mit Anlagenauswahl und GA-Funktionsliste nach Blatt 4.1
 * (`DatenpunktlisteVdi`). Der Filter (asp/gewerk) bleibt beim Umschalten
 * erhalten und grenzt auch die Anlagenliste der VDI-Ansicht ein.
 */
export function Datenpunkte({ api, projektId, asps, anlagen }: {
  api: ApiClient; projektId: string; asps: MsrAspZeile[]; anlagen: MsrAnlageZeile[];
}) {
  const [suchparams, setSuchparams] = useSearchParams();
  const ansicht = suchparams.get('ansicht') === 'vdi' ? 'vdi' : 'flach';
  const ansichtSetzen = (a: 'flach' | 'vdi') => {
    const neu = new URLSearchParams(suchparams);
    if (a === 'flach') neu.delete('ansicht'); else neu.set('ansicht', a);
    setSuchparams(neu, { replace: true });
  };

  const [filter, setFilter] = useState<DatenpunkteFilter>(DATENPUNKTE_FILTER_LEER);
  const [seite, setSeite] = useState(0);
  const [ergebnis, setErgebnis] = useState<MsrSeite<MsrDatenpunktZeile> | null>(null);
  const [laedt, setLaedt] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    setLaedt(true);
    setFehler(null);
    api.msrDatenpunkte(projektId, {
      asp: filter.asp || undefined, gewerk: filter.gewerk || undefined,
      anlage: filter.anlage || undefined, art: filter.art || undefined,
      herkunft: filter.herkunft || undefined, anbindung: filter.anbindung || undefined,
      suche: filter.suche || undefined, seite, groesse: GROESSE,
    }).then((r) => { setErgebnis(r); setLaedt(false); })
      .catch((e) => { setFehler(e instanceof Error ? e.message : String(e)); setLaedt(false); });
  }, [api, projektId, filter, seite]);

  const filterAendern = (f: DatenpunkteFilter) => { setFilter(f); setSeite(0); };

  // TM-8a: Die Excel-Mappe steht in der Exportleiste im Kopf - mit dem Filter ASP/Gewerk wie bisher.
  useMsrExport(RANG_SEITE, () => [msrZiele.datenpunktliste(api, projektId, {
    asp: filter.asp || undefined, gewerk: filter.gewerk || undefined,
  })], [api, projektId, filter.asp, filter.gewerk]);

  const zeilen = ergebnis?.zeilen ?? [];
  const gesamt = ergebnis?.gesamt ?? 0;
  const seiten = Math.max(1, Math.ceil(gesamt / GROESSE));

  const anlagenGefiltert = anlagen
    .filter((a) => !filter.asp || a.aspId === filter.asp)
    .filter((a) => !filter.gewerk || a.gewerk === filter.gewerk);

  const spalten: Spalte<MsrDatenpunktZeile>[] = [
    {
      schluessel: 'aks', titel: 'AKS', immerSichtbar: true, breite: '210px',
      wert: (d) => d.aks ?? null,
      inhalt: (d) => (d.aks
        ? <span style={{ fontFamily: 'monospace', fontSize: 11 }}>{d.aks}</span>
        : <em className="schwach">— nicht vergeben —</em>),
    },
    { schluessel: 'aspKennung', titel: 'ASP', wert: (d) => d.aspKennung ?? null },
    { schluessel: 'anlageKennung', titel: 'Anlage', wert: (d) => d.anlageKennung ?? null },
    { schluessel: 'aggregat', titel: 'Aggregat', wert: (d) => d.aggregat },
    { schluessel: 'bezeichnung', titel: 'Bezeichnung', wert: (d) => d.bezeichnung },
    { schluessel: 'art', titel: 'Art', wert: (d) => ART[d.art] },
    { schluessel: 'signal', titel: 'Signal', wert: (d) => d.signal ?? null },
    { schluessel: 'anbindung', titel: 'Anbindung', wert: (d) => d.anbindung },
    { schluessel: 'klemmen', titel: 'Klemmen', art: 'zahl', wert: (d) => d.klemmen },
    {
      schluessel: 'herkunft', titel: 'Herkunft', art: 'status', breite: '110px',
      wert: (d) => HERKUNFT_FARBE[d.herkunft].text,
      inhalt: (d) => <StatusBadge {...herkunftMarke(d.herkunft)} klein />,
    },
    { schluessel: 'gewerk', titel: 'Quelle', wert: (d) => GEWERK[d.gewerk] },
    { schluessel: 'geschoss', titel: 'Geschoss', wert: (d) => d.geschoss ?? null },
    { schluessel: 'raumnummer', titel: 'Raum', wert: (d) => d.raumnummer ?? null },
  ];

  return (
    <>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 10 }}>
        <span className="schwach" style={{ fontSize: 12 }}>Ansicht:</span>
        <button type="button" className={`reiter-knopf${ansicht === 'flach' ? ' aktiv' : ''}`}
          style={{ fontSize: 12 }} onClick={() => ansichtSetzen('flach')}>
          flach
        </button>
        <button type="button" className={`reiter-knopf${ansicht === 'vdi' ? ' aktiv' : ''}`}
          style={{ fontSize: 12 }} onClick={() => ansichtSetzen('vdi')}>
          VDI 3814
        </button>
      </div>

      {ansicht === 'flach' && (
        <>
          <Filterleiste filter={filter} onChange={filterAendern} asps={asps}
            gezeigt={zeilen.length} gesamt={gesamt} />

          {/* Die Liste blättert über den SERVER (200 je Seite). `proSeite={0}`
              schaltet das eigene Blättern der Tabelle deshalb ab, und
              `trefferText` nennt die Gesamtzahl statt der Zahl auf dieser
              Seite (Musterdoku, Abschnitt 1, letzter Absatz). */}
          <DataTable
            name="Datenpunkte"
            spalten={spalten}
            zeilen={zeilen}
            schluessel={(d) => d.id}
            trefferText={`${z(zeilen.length)} von ${z(gesamt)} Datenpunkten`}
            laedt={laedt}
            fehler={fehler ?? undefined}
            beiErneutVersuchen={() => setFilter({ ...filter })}
            dicht
            suche={false}
            klebendeErsteSpalte
            proSeite={0}
            zeilenKlasse={(d) => (d.herkunft === 'geschaetzt' ? 'datenfeld' : undefined)}
            leer={(
              <EmptyState
                titel={gesamt === 0 && seite === 0
                  ? 'Noch kein Datenpunkt abgeleitet.'
                  : 'Kein Datenpunkt passt zu diesen Filtern.'}
                erklaerung={gesamt === 0 && seite === 0
                  ? 'Die Datenpunktliste entsteht aus dem Ableitungslauf über die '
                    + 'aufgenommenen Gewerke.'
                  : 'Die gesetzten Filter grenzen die Liste auf null Zeilen ein.'}
                aktion={(
                  <button type="button" className="m-knopf"
                    onClick={() => filterAendern(DATENPUNKTE_FILTER_LEER)}>
                    Filter zurücksetzen
                  </button>
                )}
              />
            )}
          />

          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
            <button type="button" className="m-knopf" disabled={seite <= 0}
              onClick={() => setSeite((s) => s - 1)}>← Zurück</button>
            <span className="schwach" style={{ fontSize: 12 }}>
              Seite {seite + 1} von {seiten} ({z(gesamt)} gesamt)
            </span>
            <button type="button" className="m-knopf" disabled={seite + 1 >= seiten}
              onClick={() => setSeite((s) => s + 1)}>Weiter →</button>
          </div>
        </>
      )}

      {ansicht === 'vdi' && (
        <DatenpunktlisteVdi api={api} projektId={projektId} anlagen={anlagenGefiltert} />
      )}
    </>
  );
}
